import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FORMAT_DIMENSIONS_MM } from '../../../utils/printFormats';

// Bande de vignettes en bas de l'atelier — navigation directe vers
// n'importe quelle page sans repasser par precedente/suivante (retour
// utilisateur : gain d'ergonomie important sur un livre de 16+ pages, ou
// atteindre la page 14 depuis la page 2 demandait 12 clics).
//
// 2026-10-09 : vraies miniatures visuelles (retour utilisateur), remplacant
// les vignettes purement schematiques d'avant (numero + statut seulement —
// ce choix avait ete fait suite a un retour anterieur, "je ne sais pas s'il
// est pertinent d'afficher les images ici", et pour eviter un cout de rendu
// HTML/iframe par page). La nouvelle version ne reouvre PAS ce cout : aucun
// rendu HTML serveur ni PDF ici, uniquement un pavage CSS reconstruit a
// partir de donnees deja en memoire — voir `preview` dans
// BookAtelierLuxe.js:finishStats (geometrie en % de atelierLayoutGeometry.js
// + metadata.thumbnailUrl des items, le JPEG 480px deja genere a l'upload).
// Zero appel reseau supplementaire ; les images elles-memes profitent du
// chargement differe natif (`loading="lazy"`) et d'un repli silencieux vers
// la cellule neutre en cas d'echec (onError plus bas).
//
// Vit dans le tiroir "Pages" (voir BookAtelierLuxe.js, AtelierDrawer) depuis
// la refonte en tiroirs contextuels du 2026-09-26 — plus "toujours visible"
// comme a l'origine, mais accessible depuis n'importe quelle vue
// (couverture/interieur/4e) via cet onglet, contrairement a la navigation
// precedente/suivante qui vit dans la colonne centrale et change de forme
// selon viewKind.

// Alignees sur les memes constantes que AtelierBookView.js/AtelierLayoutPanel.js
// (convention deja etablie dans ce projet pour ces petites tables format ->
// dimensions plutot qu'un import partage). 2026-09-09 : realignees sur le
// catalogue reel Gelato, voir l'en-tete de coverFormat.js.

// Type de donnee PROPRE au deplacement de page. Volontairement distinct de
// l'`application/json` utilise par la barre laterale pour glisser un souvenir
// (AtelierSidebar) : sans ca, lacher une photo sur le filmstrip aurait ete
// interprete comme un deplacement de page.
const PAGE_DRAG_TYPE = 'application/x-bookipix-page';

// Apercu AGRANDI d'une page au survol de sa vignette (2026-10-10, retour
// utilisateur : "comme on fait avec les photos" — voir AtelierSidebar,
// PhotoHoverPreview, meme delai et meme logique de bascule gauche/droite).
// Contrairement aux miniatures ci-dessus (reconstruction CSS legere), cet
// apercu affiche le VRAI rendu serveur (onRequestPreview, fourni par
// BookAtelierLuxe.js — reutilise le meme cache que la vue centrale) : plus
// couteux qu'une miniature, donc jamais precharge, seulement au moment reel
// du survol.
const APERCU_LARGEUR = 220;
const APERCU_MARGE = 12;
const APERCU_DELAI_MS = 220;

function FilmstripHoverPreview({ apercu, aspectRatio }) {
  if (!apercu || apercu.html == null) return null;
  const hauteur = Math.round(APERCU_LARGEUR / (() => {
    const [l, h] = String(aspectRatio).split('/').map(Number);
    return l && h ? l / h : 1;
  })());
  const placeADroite = apercu.rect.right + APERCU_MARGE + APERCU_LARGEUR <= window.innerWidth;
  const left = placeADroite
    ? apercu.rect.right + APERCU_MARGE
    : Math.max(APERCU_MARGE, apercu.rect.left - APERCU_MARGE - APERCU_LARGEUR);
  // Au-dessus de la vignette, jamais en dessous : le filmstrip est tout en
  // bas du tiroir, un apercu pose sous la vignette sortirait de l'ecran.
  const top = Math.max(APERCU_MARGE, apercu.rect.top - hauteur - APERCU_MARGE);
  return (
    <div className="atelier-filmstrip-hover-preview" style={{ left, top, width: APERCU_LARGEUR, height: hauteur }}>
      <iframe title={apercu.label} srcDoc={apercu.html} />
    </div>
  );
}

// VRAIE MINIATURE (refonte de l'atelier, phase 2) : le rendu du serveur pour
// cette page (le meme que celui du livre ouvert), reduit. Avant, un pavage CSS
// schematique reconstruit depuis la geometrie des emplacements : il ne montrait
// ni le texte, ni les couvertures, ni la mise en forme reelle.
//
// Tant que le rendu n'est pas arrive : un squelette gris neutre, jamais un cadre
// vide. Une fois arrive, la DERNIERE version connue reste affichee pendant
// qu'une nouvelle se charge (pas de retour au gris a chaque modification).
// Le rendu n'est demande que pour les vignettes VISIBLES (voir
// onVisibility/IntersectionObserver plus bas), 4 requetes au plus en parallele
// (store/previewLoader.js).
function ThumbFrame({ html, label, widthPx, heightPx }) {
  const boxRef = useRef(null);
  const [scale, setScale] = useState(0);
  const lastHtmlRef = useRef(null);
  if (html != null) lastHtmlRef.current = html;
  const shown = lastHtmlRef.current;

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;
    const apply = () => { if (box.clientWidth > 0) setScale(box.clientWidth / widthPx); };
    apply();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(apply);
    observer.observe(box);
    return () => observer.disconnect();
  }, [widthPx]);

  return (
    <span ref={boxRef} className="atelier-filmstrip-render" aria-hidden="true">
      {shown != null && scale > 0 ? (
        <iframe
          title={label}
          tabIndex={-1}
          srcDoc={shown}
          style={{ width: widthPx, height: heightPx, transform: `scale(${scale})` }}
        />
      ) : (
        <span className="atelier-skeleton" />
      )}
    </span>
  );
}

function FilmstripCell({
  target, label, status, isActive, aspectRatio, onSelect,
  // Nombre de photos signalees sur CETTE page (0 = rien a signaler) : alerte FORTE (< 100 dpi) et
  // simple point d'attention (100-150 dpi).
  qualityWarnings = 0,
  limitWarnings = 0,
  // { show(rect, text, level), hide() } — bulle personnalisee de l'alerte (jamais une bulle native).
  onAlertBubble,
  // Deplacement : seules les pages interieures sont concernees (une
  // couverture ne se deplace pas), d'ou `onMove` absent sur les autres.
  onMove, dropSide, onDragOverCell, onDragLeaveCell, isDragging,
  // { isSpread, spreadSide } — sert a fusionner visuellement les deux pages d'une
  // double page (voir .is-spread-left/.is-spread-right).
  preview,
  // Rendu serveur de cette page (null tant qu'il n'est pas arrive), sa taille
  // naturelle en px, et le signal "visible/non visible" pour ne charger que ce
  // qui est a l'ecran.
  html, widthPx, heightPx, onVisibility,
  // { start(event, target, label), end(target) } — apercu agrandi au survol
  // (voir FilmstripHoverPreview). Present sur TOUTES les cellules, y compris
  // couverture/4e (un apercu grandeur reelle leur profite aussi).
  onHover
}) {
  const { t } = useTranslation('atelier');
  const ref = useRef(null);

  // Fait defiler la bande pour garder la vignette active visible, y compris
  // quand la navigation se fait via precedente/suivante (pas seulement un
  // clic direct dans le filmstrip) — sinon la selection "invisible" hors
  // champ romprait la coherence entre le filmstrip et la vue centrale.
  //
  // On deplace NOUS-MEMES le defilement horizontal de la bande, au lieu
  // d'appeler scrollIntoView. Celui-ci fait defiler TOUS les ancetres
  // scrollables, document compris : des que la bande n'etait pas entierement
  // visible, chaque "page suivante" faisait descendre la fenetre jusqu'a
  // elle, et le livre sortait du champ (signale le 2026-09-13 : "on est
  // renvoyes vers les pages timeline"). `block: 'nearest'` ne protege pas de
  // ca — il evite le defilement vertical seulement quand l'element est DEJA
  // entierement visible.
  useEffect(() => {
    if (!isActive) return;
    const cell = ref.current;
    const strip = cell?.parentElement;
    if (!cell || !strip) return;
    // Calcul par rectangles plutot que par offsetLeft : la bande n'est pas
    // positionnee (pas de position:relative), offsetLeft se rapporterait donc
    // a un ancetre quelconque et le centrage serait faux.
    const cellRect = cell.getBoundingClientRect();
    const stripRect = strip.getBoundingClientRect();
    const delta = (cellRect.left - stripRect.left) - (strip.clientWidth - cellRect.width) / 2;
    const maxLeft = strip.scrollWidth - strip.clientWidth;
    strip.scrollTo({ left: Math.max(0, Math.min(strip.scrollLeft + delta, maxLeft)), behavior: 'smooth' });
  }, [isActive]);

  // Visible dans la bande (avec une marge) -> on le dit au parent, qui demande
  // le rendu. Sans IntersectionObserver (vieux navigateur), tout est "visible".
  useEffect(() => {
    const cell = ref.current;
    if (!cell || !onVisibility) return undefined;
    if (typeof IntersectionObserver === 'undefined') { onVisibility(target, true); return undefined; }
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => onVisibility(target, entry.isIntersecting)),
      { root: cell.parentElement, rootMargin: '0px 240px' }
    );
    observer.observe(cell);
    return () => { observer.disconnect(); onVisibility(target, false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const movable = Boolean(onMove);
  const statusText = status ? t(`pageFilmstrip.status.${status}`) : '';
  const base = movable ? t('pageFilmstrip.pagePrefix', { label }) : String(label);
  // Correctif 11 : UN seul type d'alerte — un point orange, seulement si le probleme est reel. Le detail
  // s'affiche dans une bulle a cote de la miniature (voir AtelierPageFilmstrip), jamais dans une bulle native.
  const alertLevel = qualityWarnings > 0 ? 'strong' : (limitWarnings > 0 ? 'soft' : null);
  const alertText = qualityWarnings > 0
    ? t('pageFilmstrip.alertStrong', { count: qualityWarnings })
    : (limitWarnings > 0 ? t('pageFilmstrip.alertSoft', { count: limitWarnings }) : '');
  const ariaLabel = [base, statusText, alertText].filter(Boolean).join(' — ');
  const showBubble = () => { if (alertLevel && onAlertBubble && ref.current) onAlertBubble.show(ref.current.getBoundingClientRect(), alertText, alertLevel); };
  const hideBubble = () => { if (onAlertBubble) onAlertBubble.hide(); };

  return (
    <button
      ref={ref}
      type="button"
      className={[
        'atelier-filmstrip-cell',
        `is-${status}`,
        isActive ? 'is-active' : '',
        movable ? 'is-movable' : '',
        qualityWarnings > 0 ? 'has-quality-warning' : (limitWarnings > 0 ? 'has-quality-note' : ''),
        isDragging ? 'is-dragging' : '',
        dropSide ? `is-drop-${dropSide}` : '',
        preview?.isSpread ? `is-spread-${preview.spreadSide}` : ''
      ].filter(Boolean).join(' ')}
      draggable={movable}
      onDragStart={movable ? (event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData(PAGE_DRAG_TYPE, String(target));
        onMove.start(target);
      } : undefined}
      onDragEnd={movable ? () => onMove.end() : undefined}
      onDragOver={movable ? (event) => {
        if (!event.dataTransfer.types.includes(PAGE_DRAG_TYPE)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        // Cote survole : on depose AVANT ou APRES cette page selon que le
        // pointeur est dans sa moitie gauche ou droite. Sans ca, deposer sur
        // la derniere page ne permettrait jamais de placer quelque chose
        // apres elle.
        const rect = event.currentTarget.getBoundingClientRect();
        onDragOverCell(target, event.clientX < rect.left + rect.width / 2 ? 'before' : 'after');
      } : undefined}
      onDragLeave={movable ? () => onDragLeaveCell(target) : undefined}
      onDrop={movable ? (event) => {
        if (!event.dataTransfer.types.includes(PAGE_DRAG_TYPE)) return;
        event.preventDefault();
        onMove.drop(Number(event.dataTransfer.getData(PAGE_DRAG_TYPE)), target);
      } : undefined}
      onClick={() => onSelect(target)}
      onMouseEnter={(event) => { if (onHover) onHover.start(event, target, base); showBubble(); }}
      onMouseLeave={() => { if (onHover) onHover.end(target); hideBubble(); }}
      onFocus={showBubble}
      onBlur={hideBubble}
      aria-label={ariaLabel}
    >
      {/* Refonte 2026-10-11 (maquette "Maquettes Bookipix", ".th"/".tpg") :
          la vignette et le numero sont desormais deux elements EMPILES
          (vignette au-dessus, numero EN DESSOUS, jamais superpose) au lieu
          d'un numero flottant par-dessus l'image — c'est cette boite qui
          porte la bordure/l'arrondi/l'ombre (et la fusion de double page,
          voir .is-spread-left/.is-spread-right plus bas cote CSS), le
          bouton lui-meme reste transparent. */}
      <span className={`atelier-filmstrip-cell-thumb-box ${preview?.isSpread ? `is-spread-${preview.spreadSide}` : ''}`} style={{ aspectRatio }}>
        {/* Couche purement visuelle : jamais de `draggable`/gestionnaire
            propre (voir FilmstripSlot : draggable={false} sur chaque <img>,
            sinon le navigateur demarre son propre glisser-image natif a la
            place du deplacement de page gere par ce bouton — risque identifie
            a l'audit, pas une hypothese). */}
        <ThumbFrame html={html} label={base} widthPx={widthPx} heightPx={heightPx} />
        {/* Plus de coche (« complete » est l'etat normal) ni de triangle : un petit point orange, seulement
            quand il y a un vrai probleme (correctif 11). */}
        {alertLevel && <span className={`atelier-filmstrip-dot is-${alertLevel}`} aria-hidden="true" />}
      </span>
      <span className="atelier-filmstrip-cell-label">{label}</span>
    </button>
  );
}

function AtelierPageFilmstrip({
  pageStatuses,
  // { [pageIndex]: nombre } — photos signalees par page (voir
  // GET /print-quality-check). Absent = aucune pastille, comportement
  // d'avant inchange.
  qualityWarningsByPage = {},
  // { [pageIndex]: nombre } — photos entre 100 et 150 dpi (point d'attention, jamais bloquant).
  limitsByPage = {},
  activeTarget,
  printFormat,
  onSelect,
  onAddPages,
  addingPages,
  onRemovePages,
  removingPages,
  canRemovePages,
  minPages,
  // Deplacement de page (facultatif : sans lui, le filmstrip se comporte
  // exactement comme avant).
  onMovePage,
  movingPage,
  // (target) => Promise<html|null> — apercu agrandi au survol (facultatif :
  // sans lui, pas d'apercu, comportement d'avant inchange). Fourni par
  // BookAtelierLuxe.js, qui reutilise son propre cache de rendu.
  onRequestPreview,
  // (target) => html | null — rendu deja charge pour cette vignette.
  previewHtml,
  // (targets[]) — les vignettes actuellement visibles dans la bande.
  onVisibleTargetsChange,
  // (pageIndex) — insere 2 pages vides AVANT cette page (position d'une double
  // page : index impair seulement).
  onInsertPages,
  // (leftPageIndex) — supprime la double page dont c'est la page de gauche.
  onDeletePages,
  canDeletePages = true
}) {
  const { t } = useTranslation('atelier');
  const dims = FORMAT_DIMENSIONS_MM[printFormat] || FORMAT_DIMENSIONS_MM.standard;
  const aspectRatio = `${dims.widthMm} / ${dims.heightMm}`;
  const widthPx = Math.round(dims.widthMm * 96 / 25.4);
  const heightPx = Math.round(dims.heightMm * 96 / 25.4);

  // Vignettes visibles : agregees ici puis transmises d'un bloc (les
  // observateurs de 30 cellules ne doivent pas declencher 30 demandes).
  const visibleRef = useRef(new Set());
  const visibleTimerRef = useRef(null);
  const onVisibilityCell = useCallback((target, visible) => {
    if (visible) visibleRef.current.add(target); else visibleRef.current.delete(target);
    if (!onVisibleTargetsChange) return;
    clearTimeout(visibleTimerRef.current);
    visibleTimerRef.current = setTimeout(() => onVisibleTargetsChange([...visibleRef.current]), 80);
  }, [onVisibleTargetsChange]);
  useEffect(() => () => clearTimeout(visibleTimerRef.current), []);

  // Bulle personnalisee de l'alerte d'une miniature : posee A DROITE de la miniature (a gauche si la place
  // manque), en position fixe pour ne jamais etre coupee par la bande qui defile.
  const [bubble, setBubble] = useState(null);
  const alertBubble = {
    show: (rect, text, level) => setBubble({ rect, text, level }),
    hide: () => setBubble(null)
  };

  // « n pages a verifier » : fait defiler les pages concernees une a une.
  const flaggedPages = Object.keys({ ...limitsByPage, ...qualityWarningsByPage })
    .map(Number)
    .filter((index) => (qualityWarningsByPage[index] || 0) + (limitsByPage[index] || 0) > 0)
    .sort((a, b) => a - b);
  const goToNextFlagged = () => {
    if (!flaggedPages.length) return;
    const current = typeof activeTarget === 'number' ? activeTarget : -1;
    const next = flaggedPages.find((index) => index > current);
    onSelect(next === undefined ? flaggedPages[0] : next);
  };

  // Menu « Gerer les pages » (+2 / -2 reunis).
  const [manageOpen, setManageOpen] = useState(false);
  const manageRef = useRef(null);
  const [manageRect, setManageRect] = useState(null);
  useEffect(() => {
    if (!manageOpen) return undefined;
    const onDown = (event) => { if (!event.target.closest || !event.target.closest('.atelier-filmstrip-manage-menu, .atelier-filmstrip-manage')) setManageOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') setManageOpen(false); };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('pointerdown', onDown, true); document.removeEventListener('keydown', onKey, true); };
  }, [manageOpen]);

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // { index, side }

  // { target, rect, label, html } | null. `html` arrive apres coup (requete
  // asynchrone) — survolerRef compare le target COURANT a celui pour lequel
  // la requete a ete lancee, pour ignorer une reponse perimee si la souris a
  // deja quitte la case ou glisse vers une autre pendant le chargement.
  const [apercu, setApercu] = useState(null);
  const survolerRef = useRef(null);
  const apercuTimer = useRef(null);

  const hover = onRequestPreview ? {
    start: (event, target, label) => {
      const rect = event.currentTarget.getBoundingClientRect();
      clearTimeout(apercuTimer.current);
      apercuTimer.current = setTimeout(() => {
        survolerRef.current = target;
        setApercu({ target, rect, label, html: null });
        Promise.resolve(onRequestPreview(target)).then((html) => {
          if (survolerRef.current !== target) return; // la souris a bouge entre-temps
          setApercu((previous) => (previous?.target === target ? { ...previous, html } : previous));
        }).catch(() => {
          // Non bloquant : pas d'apercu plutot que de casser le filmstrip
          // (meme principe que refreshPagePreview cote BookAtelierLuxe.js).
        });
      }, APERCU_DELAI_MS);
    },
    end: (target) => {
      clearTimeout(apercuTimer.current);
      if (survolerRef.current === target) survolerRef.current = null;
      setApercu((previous) => (previous?.target === target ? null : previous));
    }
  } : null;

  const move = onMovePage ? {
    start: (index) => { setDraggedIndex(index); setDropTarget(null); },
    end: () => { setDraggedIndex(null); setDropTarget(null); },
    drop: (from, over) => {
      setDraggedIndex(null);
      const side = dropTarget?.index === over ? dropTarget.side : 'before';
      // Position d'insertion vue comme un "entre-deux", puis ramenee a un
      // index de destination. Retirer d'abord la page decale d'un cran tout
      // ce qui la suit : sans cette correction, deplacer une page vers la
      // droite la posait systematiquement une position trop loin.
      const insertAt = side === 'after' ? over + 1 : over;
      const to = insertAt > from ? insertAt - 1 : insertAt;
      setDropTarget(null);
      if (to !== from) onMovePage(from, to);
    }
  } : null;

  const cellMoveProps = (pageIndex) => (move ? {
    onMove: move,
    isDragging: draggedIndex === pageIndex,
    dropSide: draggedIndex != null && dropTarget?.index === pageIndex ? dropTarget.side : null,
    onDragOverCell: (index, side) => setDropTarget((previous) => (
      previous?.index === index && previous?.side === side ? previous : { index, side }
    )),
    onDragLeaveCell: (index) => setDropTarget((previous) => (previous?.index === index ? null : previous))
  } : {});

  return (
    <div className="atelier-filmstrip-shell">
    {flaggedPages.length > 0 && (
      <div className="atelier-filmstrip-head">
        <button type="button" className="atelier-filmstrip-review" onClick={goToNextFlagged}>
          <span className="atelier-filmstrip-dot is-inline" aria-hidden="true" />
          {t('pageFilmstrip.pagesToCheck', { count: flaggedPages.length })}
        </button>
      </div>
    )}
    <div className={`atelier-filmstrip ${movingPage ? 'is-moving' : ''}`}>
      {/* Couvertures : ni "complete" ni "vide" au meme sens qu'une page
          interieure (toujours un contenu par defaut) — classe neutre
          dediee plutot qu'un statut trompeur. */}
      <FilmstripCell
        target="cover"
        label={t('pageFilmstrip.coverLabel')}
        status="cover"
        isActive={activeTarget === 'cover'}
        aspectRatio={aspectRatio}
        onSelect={onSelect}
        onHover={hover}
        html={previewHtml ? previewHtml('cover') : null}
        widthPx={widthPx}
        heightPx={heightPx}
        onVisibility={onVisibilityCell}
      />
      <div className="atelier-filmstrip-sep" aria-hidden="true" />
      {pageStatuses.map(({ pageIndex, status, preview }) => (
        <React.Fragment key={pageIndex}>
          {/* "+" ENTRE deux doubles pages : insere 2 pages vides ici. Seulement
              avant une page de GAUCHE (index impair) : les nouvelles pages se
              font alors face, et toutes les suivantes gardent leur cote. */}
          {onInsertPages && pageIndex % 2 === 1 && (
            <span className="atelier-filmstrip-insert">
              <button
                type="button"
                onClick={() => onInsertPages(pageIndex)}
                disabled={addingPages}
                aria-label={t('pageFilmstrip.insertHere')}
              >
                +
              </button>
            </span>
          )}
          <FilmstripCell
            target={pageIndex}
            label={pageIndex + 1}
            status={status}
            isActive={activeTarget === pageIndex}
            aspectRatio={aspectRatio}
            onSelect={onSelect}
            qualityWarnings={qualityWarningsByPage[pageIndex] || 0}
            limitWarnings={limitsByPage[pageIndex] || 0}
            onAlertBubble={alertBubble}
            preview={preview}
            onHover={hover}
            html={previewHtml ? previewHtml(pageIndex) : null}
            widthPx={widthPx}
            heightPx={heightPx}
            onVisibility={onVisibilityCell}
            {...cellMoveProps(pageIndex)}
          />
          {/* "x" au coin de la double page : supprime ces 2 pages. Apres la page
              de DROITE d'une double page (index pair >= 2) ; grise au minimum
              imprimable, avec l'explication en infobulle. */}
          {onDeletePages && pageIndex % 2 === 0 && pageIndex >= 2 && (
            <span className="atelier-filmstrip-delete">
              <button
                type="button"
                onClick={() => onDeletePages(pageIndex - 1)}
                disabled={!canDeletePages}
                aria-label={canDeletePages
                  ? t('pageFilmstrip.deleteHere', { first: pageIndex, second: pageIndex + 1 })
                  : t('pageFilmstrip.removeTitleDisabled', { min: minPages })}
              >
                ×
              </button>
            </span>
          )}
        </React.Fragment>
      ))}
      {/* Agrandir ou reduire volontairement le livre (jamais automatique —
          voir routes/composition.js: POST /pages/extend et /pages/shrink) :
          2 pages a la fois, meme palier que le catalogue imprimeur (Gelato
          n'accepte que des nombres pairs de pages). Le retrait se fait par la
          FIN et se desactive des qu'on atteint le minimum imprimable — le
          bouton reste visible (grise, avec l'explication en infobulle)
          plutot que de disparaitre sans dire pourquoi. */}
      {(onAddPages || onRemovePages) && (
        <button
          ref={manageRef}
          type="button"
          className="atelier-filmstrip-add atelier-filmstrip-manage"
          style={{ aspectRatio }}
          onClick={() => { setManageRect(manageRef.current ? manageRef.current.getBoundingClientRect() : null); setManageOpen((open) => !open); }}
          disabled={addingPages || removingPages}
          aria-haspopup="menu"
          aria-expanded={manageOpen}
        >
          {addingPages || removingPages ? '…' : t('pageFilmstrip.manage')}
        </button>
      )}
      <div className="atelier-filmstrip-sep" aria-hidden="true" />
      <FilmstripCell
        target="back-cover"
        label={t('pageFilmstrip.backCoverLabel')}
        status="cover"
        isActive={activeTarget === 'back-cover'}
        aspectRatio={aspectRatio}
        onSelect={onSelect}
        onHover={hover}
        html={previewHtml ? previewHtml('back-cover') : null}
        widthPx={widthPx}
        heightPx={heightPx}
        onVisibility={onVisibilityCell}
      />
      <FilmstripHoverPreview apercu={apercu} aspectRatio={aspectRatio} />
    </div>
    {manageOpen && manageRect && createPortal(
      <div
        className="atelier-filmstrip-manage-menu"
        role="menu"
        style={{ left: Math.max(8, Math.min(window.innerWidth - 248, manageRect.left + manageRect.width / 2 - 120)), bottom: Math.max(8, window.innerHeight - manageRect.top + 8) }}
      >
        {onAddPages && (
          <button type="button" role="menuitem" onClick={() => { setManageOpen(false); onAddPages(); }} disabled={addingPages}>
            {t('pageFilmstrip.manageAdd')}
          </button>
        )}
        {onRemovePages && (
          <button type="button" role="menuitem" onClick={() => { setManageOpen(false); onRemovePages(); }} disabled={removingPages || !canRemovePages}>
            {t('pageFilmstrip.manageRemove')}
            {!canRemovePages && <small>{t('pageFilmstrip.removeTitleDisabled', { min: minPages })}</small>}
          </button>
        )}
      </div>,
      document.body
    )}
    {bubble && createPortal(
      <div
        className={`atelier-filmstrip-bubble is-${bubble.level}`}
        role="tooltip"
        style={{
          top: Math.max(8, bubble.rect.top),
          left: bubble.rect.right + 8 + 220 <= window.innerWidth ? bubble.rect.right + 8 : Math.max(8, bubble.rect.left - 8 - 220)
        }}
      >
        {bubble.text}
      </div>,
      document.body
    )}
    </div>
  );
}

export default AtelierPageFilmstrip;
